/**
 * A track's criterion weights (migration 1047): each Criteria & Scales row
 * (`cycle_attribute_configs.weight`) carries its share of the track's balance as
 * a percentage, or none. Canon 02 §2A: criteria and their weights live in the
 * Criteria stage.
 *
 * A track holds either a weight on every criterion, adding up to 100, or none at
 * all — then its sessions start from an even split. The track's Criteria tab and
 * the cycle configure wizard's Weights step both save through
 * `saveTrackCriteriaWeights` (attributeGovernanceStore), which checks with
 * `checkTrackWeights` first. The database holds each value to 0–100 and holds the
 * whole set while the criteria are locked.
 */
import { normalizeTo100 } from './weightAllocation';

/** Weights by attribute key; null = no weight. */
export type TrackWeights = Record<string, number | null>;

export interface TrackWeightsCheck {
  ok: boolean;
  /** Sum of the weights given. */
  total: number;
  /** Every criterion has a weight. */
  allSet: boolean;
  /** No criterion has a weight: sessions start from an even split. */
  noneSet: boolean;
  /** Why it may not be saved, in plain words. */
  error?: string;
}

const TOLERANCE = 0.01;

export function checkTrackWeights(weights: TrackWeights): TrackWeightsCheck {
  const values = Object.values(weights);
  const given = values.filter((w): w is number => w != null);
  const total = given.reduce((s, w) => s + w, 0);
  const allSet = values.length > 0 && given.length === values.length;
  const noneSet = given.length === 0;
  const base = { total, allSet, noneSet };
  if (given.some((w) => !Number.isFinite(w) || w < 0 || w > 100)) {
    return { ...base, ok: false, error: 'Each weight is a percentage from 0 to 100.' };
  }
  if (noneSet) return { ...base, ok: true };
  if (!allSet) {
    return { ...base, ok: false, error: 'Give every criterion a weight, or clear them all for an even split.' };
  }
  if (Math.abs(total - 100) > TOLERANCE) {
    return { ...base, ok: false, error: `The weights add up to ${formatWeight(total)}; they need to add up to 100.` };
  }
  return { ...base, ok: true };
}

/**
 * The weights a session on this track scores with, as shares of 1 by attribute
 * key — or null when the track sets none (a criterion without a weight, or all
 * of them 0), and its sessions split evenly or set their own.
 *
 * A session follows these (migration 1048): a new session takes them, and while
 * the track's criteria are locked every session on it scores with them, whoever
 * writes. Pass the track's rows (criteriaRowsForTrack).
 */
// reuse-ack: mirrors internal_track_criteria_weights in supabase/migrations/1048_a_session_on_a_track_scores_with_its_weights.sql — the server holds the rule, screens show it before anyone saves
export function trackWeightShares(
  rows: readonly { attributeKey: string; weight?: number | null }[],
): Record<string, number> | null {
  const byKey = new Map<string, number | null>();
  for (const row of rows) {
    if (!byKey.has(row.attributeKey)) byKey.set(row.attributeKey, row.weight == null ? null : Number(row.weight));
  }
  const weights = [...byKey.values()];
  if (weights.length === 0 || weights.some((w) => w == null || !Number.isFinite(w) || w < 0)) return null;
  const total = (weights as number[]).reduce((s, w) => s + w, 0);
  if (total <= 0) return null;
  return Object.fromEntries([...byKey].map(([k, w]) => [k, (w as number) / total]));
}

/** "Impact 40% · Effort 60%": a track's weights in words, in the order given. */
export function describeTrackWeights(
  shares: Record<string, number>,
  labelFor: (key: string) => string = (key) => key,
): string {
  return Object.entries(shares)
    .map(([key, share]) => `${labelFor(key)} ${formatWeight(share * 100)}%`)
    .join(' · ');
}

/** "40", "33.5" — a percentage without trailing zeros. */
export function formatWeight(w: number): string {
  return String(Math.round(w * 100) / 100);
}

/**
 * The balance a track with only some of the given criteria takes: the same
 * weights, scaled to 100 over the criteria it holds. Used by the wizard when one
 * balance is saved to several tracks whose criteria differ. Keys with no weight
 * stay null; when none of the track's keys has a weight, every one is null.
 */
export function weightsForTrack(weights: TrackWeights, trackKeys: readonly string[]): TrackWeights {
  const own = trackKeys.map((k) => [k, weights[k] ?? null] as const);
  if (own.length === 0 || own.some(([, w]) => w == null)) {
    return Object.fromEntries(own.map(([k]) => [k, null]));
  }
  const total = own.reduce((s, [, w]) => s + (w ?? 0), 0);
  if (Math.abs(total - 100) <= TOLERANCE) return Object.fromEntries(own);
  return normalizeTo100(Object.fromEntries(own.map(([k, w]) => [k, w ?? 0])));
}
